import React from 'react'
import { AttachmentLink, Button, Heading } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'

import { FormValues } from './initialValues'
import { Contract, UserMetadata } from '../../../common/validations'
import { useDefectLocations } from './utils'
import { DefectClass } from '../../../common/validations/defectNotice'

import { Section, TextTable } from 'components'

interface DefectNoticePreviewProps {
  contract: Contract
  formValues: FormValues
  isSubmitting: boolean
  submit: () => void
  cancel: () => void
  editMode?: boolean
  userMetadata: UserMetadata
}

export const DefectNoticePreview: React.FC<DefectNoticePreviewProps> = ({
  contract,
  formValues,
  isSubmitting,
  submit,
  cancel,
  editMode,
  userMetadata: { isImpersonator },
}) => {
  const { t } = useTranslation(['common', 'defectNotice'])
  const defectLocations = useDefectLocations(contract)

  const defectLocation = defectLocations.find(
    ({ id }) => id === formValues.defectLocation
  )?.label

  const defectClass = formValues.defectClassId
    ? t(
        `defectNotice:defectClasses.${
          formValues.defectClassId as unknown as DefectClass
        }`
      )
    : t('defectNotice:defectClasses.-1')

  const {
    title,
    description,
    files,
    masterKeyAccessAllowed,
    hasPetsInApartment,
  } = formValues

  const handleCancel = () => {
    cancel()
  }

  const handleSubmit = () => {
    submit()
  }

  return (
    <>
      <div className="bg-brand-1 py-8 rounded-t-lg -mt-7 -mx-7 mb-10 text-center">
        <Heading size="l" element="h1">
          {t('defectNotice:headings.preview')}
        </Heading>
      </div>
      <Section className="mb-12">
        <TextTable className="table-fixed w-full min-tablet:text-lg">
          <TextTable.Row>
            <TextTable.Cell>
              {t('defectNotice:labels.defectLocation')}
            </TextTable.Cell>
            <TextTable.Cell>{defectLocation}</TextTable.Cell>
          </TextTable.Row>
          <TextTable.Row>
            <TextTable.Cell>
              {t('defectNotice:labels.defectClass')}
            </TextTable.Cell>
            <TextTable.Cell>{defectClass}</TextTable.Cell>
          </TextTable.Row>
          <TextTable.Row>
            <TextTable.Cell>{t('defectNotice:labels.title')}</TextTable.Cell>
            <TextTable.Cell className="whitespace-pre-wrap break-words">
              {title}
            </TextTable.Cell>
          </TextTable.Row>
          <TextTable.Row>
            <TextTable.Cell>
              {t('defectNotice:labels.description')}
            </TextTable.Cell>
            <TextTable.Cell className="whitespace-pre-wrap break-words">
              {description}
            </TextTable.Cell>
          </TextTable.Row>
          <TextTable.Row>
            <TextTable.Cell>{t('defectNotice:labels.files')}</TextTable.Cell>
            <TextTable.Cell>
              {files.length
                ? files.map((file) => (
                    <AttachmentLink
                      key={file.fileName}
                      href={file._objectUrl}
                      text={file.metadata?.originalFileName ?? file.fileName}
                      icon={file.fileType === 'image' ? 'image' : 'file'}
                      target="_blank"
                      className="mb-3 last:mb-0 break-all"
                    />
                  ))
                : '-'}
            </TextTable.Cell>
          </TextTable.Row>
          <TextTable.Row>
            <TextTable.Cell>
              {t('defectNotice:labels.masterKeyAccessAllowed')}
            </TextTable.Cell>
            <TextTable.Cell>
              {masterKeyAccessAllowed ? t('common:yes') : t('common:no')}
            </TextTable.Cell>
          </TextTable.Row>
          <TextTable.Row>
            <TextTable.Cell>
              {t('defectNotice:labels.hasPetsInApartment')}
            </TextTable.Cell>
            <TextTable.Cell>
              {hasPetsInApartment ? t('common:yes') : t('common:no')}
            </TextTable.Cell>
          </TextTable.Row>
        </TextTable>
      </Section>
      <div className="mt-16 flex items-center justify-end">
        <Button type="button" variant="cancel" onClick={() => handleCancel()}>
          {t('defectNotice:actions.back')}
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={() => handleSubmit()}
          disabled={isImpersonator || isSubmitting}
          loading={isSubmitting}
        >
          {editMode ? t('common:save') : t('defectNotice:actions.sendForm')}
        </Button>
      </div>
    </>
  )
}
